import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { restCacheConfig } from '../config/axios';
import { QUERY_KEYS } from '../hooks/hooksUtils';
import { useInfiniteQueryData } from '../hooks/useInfiniteQueryData';
import { useQueryData } from '../hooks/useQueryData';
import { useUserStore } from '../stores/UserStore';
import { RestService } from './RestService';

export enum CLIENT_TYPES {
  CLIENT = 'CLIENT',
  VENDOR = 'VENDOR'
}

export const fetchClients = ({ queryKey, pageParam }) => {
  return RestService.get(`/clients?type=${queryKey[1]}&page=${pageParam}`);
};

export const useClientsData = type => {
  return useInfiniteQueryData([QUERY_KEYS.CLIENTS, type], fetchClients, restCacheConfig, true);
};

export const fetchClientsAll = ({ queryKey }) => {
  return RestService.get(`/clients/all?type=${queryKey[1]}`);
};

export const useClientsAllData = type => {
  return useQueryData([QUERY_KEYS.CLIENTS_ALL, type], fetchClientsAll, restCacheConfig);
};

export const useAddClientMutation = (type: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (asset: any) => {
      const userUuid = useUserStore.getState().UUID;
      const businessUuid = useUserStore.getState().UBUUID;
      const data = _.assign(asset, {
        userUuid,
        businessUuid,
        uuid: uuidv4()
      });
      return RestService.post('/clients', data);
    },
    onSuccess: async (newData: any) => {
      const queryKey = [QUERY_KEYS.CLIENTS, type];
      const queryKey2 = [QUERY_KEYS.CLIENTS_ALL, type];
      await queryClient.cancelQueries({ queryKey });
      const oldData = queryClient.getQueryData(queryKey);
      if (!!oldData) {
        queryClient.setQueryData(queryKey, (oldQueryData: any) => {
          return {
            ...oldQueryData,
            pages: oldQueryData.pages.map((pageData: any, index: number) => {
              return {
                ...pageData,
                data: index === 0 ? [newData.data, ...pageData.data] : pageData.data
              };
            })
          };
        });
      }
      const oldData2 = queryClient.getQueryData(queryKey2);
      if (!!oldData2) {
        queryClient.setQueryData(queryKey2, (oldQueryData: any) => {
          return {
            ...oldQueryData,
            data: [newData.data, ...oldQueryData.data]
          };
        });
      }
      return { oldData };
    }
  });
};

export const useUpdateClientMutation = (type: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => {
      const userUuid = useUserStore.getState().UUID;
      const updatedClient = _.assign(data, { userUuid });
      return RestService.update('/clients/' + data.uuid, updatedClient);
    },
    onSuccess: async (newData: any) => {
      const queryKey = [QUERY_KEYS.CLIENTS, type];
      const queryKey2 = [QUERY_KEYS.CLIENTS_ALL, type];
      await queryClient.cancelQueries({ queryKey });
      const oldData = queryClient.getQueryData(queryKey);
      if (!!oldData) {
        queryClient.setQueryData(queryKey, (oldQueryData: any) => {
          return {
            ...oldQueryData,
            pages: oldQueryData.pages.map((pageData: any) => {
              return {
                ...pageData,
                data: pageData.data.map(item => {
                  if (item.uuid === newData.data.uuid) {
                    return { ...item, ...newData.data };
                  }
                  return item;
                })
              };
            })
          };
        });
      }
      const oldData2 = queryClient.getQueryData(queryKey2);
      if (!!oldData2) {
        queryClient.setQueryData(queryKey2, (oldQueryData: any) => {
          return {
            ...oldQueryData,
            data: oldQueryData.data.map(item => (item.uuid === newData.data.uuid ? newData.data : item))
          };
        });
      }
      return { oldData };
    }
  });
};

export const useDeleteClientMutation = (type: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => RestService.delete('/clients/' + uuid),
    onSuccess: async (newData: any, uuid) => {
      const queryKey = [QUERY_KEYS.CLIENTS, type];
      const queryKey2 = [QUERY_KEYS.CLIENTS_ALL, type];
      await queryClient.cancelQueries({ queryKey });
      const oldData = queryClient.getQueryData(queryKey);
      if (!!oldData) {
        queryClient.setQueryData(queryKey, (oldQueryData: any) => {
          return {
            ...oldQueryData,
            pages: oldQueryData.pages.map((pageData: any) => {
              return {
                ...pageData,
                data: pageData.data.filter(item => item.uuid !== uuid)
              };
            })
          };
        });
      }
      const oldData2 = queryClient.getQueryData(queryKey2);
      if (!!oldData2) {
        queryClient.setQueryData(queryKey2, (oldQueryData: any) => {
          return {
            ...oldQueryData,
            data: oldQueryData.data.filter(item => item.uuid === uuid)
          };
        });
      }
      return { oldData };
    }
  });
};

export const useImportClientsMutation = type => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (clients: any) => {
      const userUuid = useUserStore.getState().UUID;
      const businessUuid = useUserStore.getState().UBUUID;
      const data = clients.map(client =>
        Object.assign(client, {
          type,
          userUuid,
          businessUuid,
          uuid: uuidv4()
        })
      );
      return RestService.post('/clients/import', data);
    },
    onSuccess: async () => {
      const queryKey = [QUERY_KEYS.CLIENTS, type];
      const queryKey2 = [QUERY_KEYS.CLIENTS_ALL, type];
      await queryClient.invalidateQueries({ queryKey });
      await queryClient.invalidateQueries({ queryKey: queryKey2 });
    }
  });
};
